import React from 'react';

import { useFormContext } from 'react-hook-form';
import { Collapse, Grid, Stack } from '@mui/material';

import { DateTime } from 'luxon';

import {
  AuditLog,
  Employee,
  EmployeePosition,
  FlightOperationNotification, FlightOperationNotificationVersionGroup,
  FormGenerator,
  ReportType,
} from '../../../core/api/clients';

import AttachmentList from '../../../components/AttachmentList';
import ControlledTextField from '../../../components/ControlledTextField';

import { useFlightOperationNotificationData } from '../../../core/context/FlightOperationNotificationDataContext';
import ReadOnlyField from '../../../components/ReadOnlyField';

interface NarrativeStepProps {
  visible: boolean;
  readOnly?: boolean;
  isPriorVersion?: boolean;
}

type NarrativeVersion = {
  version: number,
  maxVersion: number,
  createdBy: string,
  createdOn: DateTime,
  narrative: string,
};

function NarrativeStep({ visible, readOnly, isPriorVersion }: NarrativeStepProps) {
  const [narrativeVersions, setNarrativeVersions] = React.useState<NarrativeVersion[]>([]);
  const { control, setValue } = useFormContext<FlightOperationNotification>();

  const {
    flightOperationNotification,
    allowedActions,
    flightOperationNotificationVersionGroups,
    selectedVersion,
  } = useFlightOperationNotificationData();

  React.useEffect(() => {
    if (flightOperationNotificationVersionGroups) {
      const allVersionNumbers = flightOperationNotificationVersionGroups.map((g) => g.version?.versionNumber ?? 0);
      const latestVersion = Math.max(...allVersionNumbers);
      const versionTarget = selectedVersion ?? latestVersion;

      const narratives = flightOperationNotificationVersionGroups.reduce((acc, versionGroup) => {
        if ((versionGroup.version?.versionNumber ?? 0) < versionTarget) {
          const withKey = (key: string) => (auditLog: AuditLog) => auditLog.key === key;

          if (versionGroup.auditLogs?.some(withKey('Narrative'))) {
            return [...acc, {
              version: versionGroup.version?.versionNumber ?? 0,
              maxVersion: latestVersion,
              createdBy: versionGroup.version?.createdBy ?? '',
              createdOn: DateTime.fromISO(versionGroup.version?.versionDate ?? ''),
              narrative: versionGroup.auditLogs?.find(withKey('Narrative'))?.newValue ?? '',
            }];
          }
        }

        return acc;
      }, [] as NarrativeVersion[]);

      setNarrativeVersions(narratives.sort((a, b) => b.version - a.version));
    }
  }, [flightOperationNotificationVersionGroups, selectedVersion]);

  return (
    <Collapse in={visible}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ControlledTextField<FlightOperationNotification>
            control={control}
            name="narrative"
            label={narrativeVersions.length > 0 ? 'Latest Narrative' : 'Narrative'}
            TextFieldProps={{
              multiline: true,
              rows: 6,
            }}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit || isPriorVersion}
          />
        </Grid>
        {readOnly && narrativeVersions.length > 0 && (
          <Grid item xs={12}>
            {narrativeVersions.map((narrativeVersion) => (
              <Stack spacing={2}>
                <ReadOnlyField
                  key={narrativeVersion.version}
                  name={`Narrative v${narrativeVersion.version} of ${narrativeVersion.maxVersion} [Submitted by ${narrativeVersion.createdBy} on ${narrativeVersion.createdOn.toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)}]`}
                  value={narrativeVersion.narrative}
                />
              </Stack>
            ))}
          </Grid>
        )}
        <Grid item xs={12}>
          <AttachmentList
            reportType={ReportType.FlightOperationNotification}
            reportId={flightOperationNotification.id}
            allowedActions={allowedActions}
            disableUpload={!allowedActions?.edit || isPriorVersion}
            hideUpload={!allowedActions?.edit || isPriorVersion}
          />
        </Grid>
      </Grid>
    </Collapse>
  );
}

export default NarrativeStep;
